import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageShell } from '../layout/page-shell.jsx';
import { OutcomeBadge } from '../ui/badges.jsx';
import { formatUtcInTimezone } from '../../lib/time.js';
import { api } from '../../api/index.js';

/**
 * TMS ADMS — Coordinator Dashboard.
 *
 * Single API call (`/api/dashboard`) fans out to every domain getStats() so
 * the page renders without N waterfall requests. Polls every 30s to keep
 * counters and the activity feed live without thrashing the server.
 *
 * Layout strategy:
 *   1. Hero strip — license pill + build version, instant signal that the
 *      coordinator is healthy and licensed.
 *   2. Counter row — Nodes / Units / Persons / Channels / Certificates,
 *      each with a sub-line breakdown (tier / persons-with-device / etc).
 *   3. Two-column body — left: CA + cert expiry warnings + policy version,
 *      right: recent audit events + recent notifications.
 *   4. Coverage strip — top countries by node count + a tiny inline map
 *      preview. Map is rendered via plain MapLibre with the existing TMS
 *      Layers control so visuals are consistent with the full /map page.
 */

const REFRESH_MS = 30_000;

const EVENT_ICONS = {
  'auth.login':                  'fa-right-to-bracket',
  'auth.logout':                 'fa-arrow-right-from-bracket',
  'user.create':                 'fa-user-plus',
  'user.update':                 'fa-user-pen',
  'user.update.self':            'fa-user-pen',
  'user.delete':                 'fa-user-minus',
  'user.password.reset':         'fa-key',
  'user.password.change.self':   'fa-key',
  'user.bootstrap':              'fa-user-shield',
  'settings.update':             'fa-sliders',
  'logs.clear':                  'fa-trash-can',
  'node.create':                 'fa-network-wired',
  'node.update':                 'fa-network-wired',
  'node.delete':                 'fa-network-wired',
  'unit.create':                 'fa-sitemap',
  'unit.update':                 'fa-sitemap',
  'channel.create':              'fa-comments',
  'channel.update':              'fa-comments',
  'license.renew':               'fa-shield-halved',
};
function eventIcon(t) { return EVENT_ICONS[t] || 'fa-circle-dot'; }

const STATUS_TONE = {
  active:                'good',
  active_with_warning:   'warn',
  time_unsynced:         'warn',
  initializing:          'mute',
  not_activated:         'bad',
  not_configured:        'bad',
  ipc_unreachable:       'bad',
  expired:               'bad',
  revoked:               'bad',
  hardware_mismatch:     'bad',
  software_uuid_mismatch:'bad',
  signature_invalid:     'bad',
  tampering_detected:    'bad',
  unsupported_token_version: 'bad',
  unsupported_crypto_suite:  'bad',
};
const TONE_CLASSES = {
  good: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  warn: 'border-amber-200   bg-amber-50   text-amber-700',
  bad:  'border-rose-200    bg-rose-soft  text-rose-700',
  mute: 'border-line        bg-canvas     text-mist',
};
const STATUS_LABEL = {
  active: 'Active', active_with_warning: 'Active (warning)', time_unsynced: 'Time desync',
  initializing: 'Initializing', not_activated: 'Not activated', not_configured: 'Not configured',
  ipc_unreachable: 'IPC unreachable', expired: 'Expired', revoked: 'Revoked',
  hardware_mismatch: 'Hardware mismatch', software_uuid_mismatch: 'UUID mismatch',
  signature_invalid: 'Signature invalid', tampering_detected: 'Tampering',
  unsupported_token_version: 'Unsupported version', unsupported_crypto_suite: 'Unsupported crypto',
};

function formatRelative(iso) {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  const diffSec = Math.round((Date.now() - t) / 1000);
  if (diffSec < 60)        return diffSec <= 1 ? 'just now' : `${diffSec}s ago`;
  if (diffSec < 3600)      return `${Math.round(diffSec / 60)}m ago`;
  if (diffSec < 86_400)    return `${Math.round(diffSec / 3600)}h ago`;
  if (diffSec < 86_400 * 30) return `${Math.round(diffSec / 86_400)}d ago`;
  return new Date(t).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function daysUntil(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return Math.ceil((t - Date.now()) / 86_400_000);
}

// ─── Page ─────────────────────────────────────────────────────────────

export function DashboardPage({ currentUser }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const r = await api.dashboard.summary();
      setData(r);
      setError(null);
    } catch (err) {
      setError(err?.payload?.error || err.message || 'Failed to load dashboard.');
    } finally {
      if (!silent) setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load(false);
    const id = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  const tz = currentUser?.timezone || 'UTC';

  return (
    <PageShell
      icon="fa-gauge-high"
      title={`Welcome${currentUser?.displayName ? ', ' + currentUser.displayName : currentUser?.login ? ', ' + currentUser.login : ''}`}
      subtitle="TMS ADMS Coordinator — operational overview"
      actions={
        <button type="button" onClick={() => load(false)} disabled={refreshing} className="btn-secondary">
          <i className={`fa-solid fa-arrows-rotate ${refreshing ? 'animate-spin' : ''}`} aria-hidden="true" />
          Refresh
        </button>
      }
    >
      {error && (
        <div role="alert" className="card mb-4 border-rose-200 bg-rose-soft px-5 py-4 text-sm text-rose-700">
          <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
          {error}
        </div>
      )}

      {!data ? (
        <div className="card px-6 py-16 text-center text-mist">
          <span className="loader-spinner mx-auto mb-3 text-accent" aria-hidden="true" />
          <div className="text-sm">Loading dashboard…</div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* ── Hero strip: license + build + server time + policy ── */}
          <HeroStrip data={data} tz={tz} />

          {/* ── Counter cards ── */}
          <CountersGrid c={data.counters} />

          {/* ── Two-column body ── */}
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <div className="space-y-6 xl:col-span-2">
              <CoverageMap nodes={data.map?.nodes || []} units={data.map?.units || []} />
              <CertificatesPanel ca={data.ca} stats={data.counters.certificates} />
            </div>
            <div className="space-y-6">
              <ActivityFeed events={data.audit || []} tz={tz} />
              <NotificationsFeed items={data.notifications || []} tz={tz} />
            </div>
          </div>

          {/* ── Footer status strip ── */}
          <FooterStrip data={data} tz={tz} />
        </div>
      )}
    </PageShell>
  );
}

// ─── Hero strip ───────────────────────────────────────────────────────

function HeroStrip({ data, tz }) {
  const lic = data.license || {};
  const tone = STATUS_TONE[lic.status] || 'mute';
  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-4">
      <div className="card flex items-center gap-4 px-5 py-4 lg:col-span-2">
        <div className={`flex h-11 w-11 items-center justify-center rounded-2xl border ${TONE_CLASSES[tone]}`}>
          <i className="fa-solid fa-shield-halved text-lg" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-mist">License</div>
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="text-base font-semibold text-ink">
              {STATUS_LABEL[lic.status] || lic.status || 'Unknown'}
            </span>
            {lic.licenseId && <code className="font-mono text-xs text-mist">{lic.licenseId}</code>}
          </div>
          <div className="mt-0.5 text-xs text-mist">
            {lic.isLifetime ? (
              <><i className="fa-solid fa-infinity mr-1" /> Lifetime license</>
            ) : lic.expiresAt ? (
              <>Expires <strong className="text-ink">{formatUtcInTimezone(lic.expiresAt, tz)}</strong></>
            ) : '—'}
          </div>
        </div>
        <Link to="/settings#license" className="text-mist hover:text-accent" title="Open license settings">
          <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true" />
        </Link>
      </div>

      <KpiCard
        icon="fa-box"
        label="Coordinator build"
        value={data.build?.version || 'dev'}
        sub={data.build?.builtAt ? `Built ${formatRelative(data.build.builtAt)}` : null}
        mono
      />
      <KpiCard
        icon="fa-broadcast-tower"
        label="Policy"
        value={data.policy?.version != null ? `v${data.policy.version}` : '—'}
        sub={
          data.policy?.lastChangeAt
            ? `Last bump ${formatRelative(data.policy.lastChangeAt)} by ${data.policy.lastChangeBy || 'system'}`
            : 'No mutations yet'
        }
      />
    </div>
  );
}

function KpiCard({ icon, label, value, sub, mono }) {
  return (
    <div className="card flex items-center gap-4 px-5 py-4">
      <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-accent-soft text-accent">
        <i className={`fa-solid ${icon} text-lg`} aria-hidden="true" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-mist">{label}</div>
        <div className={`text-base font-semibold text-ink ${mono ? 'font-mono' : ''}`}>{value}</div>
        {sub && <div className="mt-0.5 truncate text-xs text-mist">{sub}</div>}
      </div>
    </div>
  );
}

// ─── Counters ─────────────────────────────────────────────────────────

function CountersGrid({ c }) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      <CounterCard
        to="/nodes"
        icon="fa-network-wired"
        accent="indigo"
        label="Nodes"
        value={c.nodes?.total ?? 0}
        breakdown={[
          { label: 'C4ISR', value: c.nodesByTier?.C4ISR ?? 0 },
          { label: 'C2',    value: c.nodesByTier?.C2    ?? 0 },
          { label: 'C1',    value: c.nodesByTier?.C1    ?? 0 },
        ]}
      />
      <CounterCard
        to="/oss"
        icon="fa-sitemap"
        accent="emerald"
        label="Units"
        value={c.units?.units ?? 0}
        breakdown={[
          { label: 'Roots', value: c.units?.rootUnits ?? 0 },
        ]}
      />
      <CounterCard
        to="/personnel"
        icon="fa-users"
        accent="violet"
        label="Persons"
        value={c.units?.persons ?? 0}
        breakdown={[
          { label: 'With device', value: c.units?.personsWithDevice ?? 0 },
        ]}
      />
      <CounterCard
        to="/channels"
        icon="fa-comments"
        accent="cyan"
        label="Channels"
        value={c.channels?.total ?? 0}
        breakdown={[
          { label: 'OSS',    value: c.channels?.oss    ?? 0 },
          { label: 'Custom', value: c.channels?.custom ?? 0 },
        ]}
      />
      <CounterCard
        to="/certificates"
        icon="fa-shield"
        accent={c.certificates?.expiringSoon > 0 ? 'amber' : 'sky'}
        label="Certificates"
        value={c.certificates?.active ?? 0}
        breakdown={[
          { label: 'Expiring 14d', value: c.certificates?.expiringSoon ?? 0, warn: (c.certificates?.expiringSoon ?? 0) > 0 },
          { label: 'Revoked',      value: c.certificates?.revoked      ?? 0 },
        ]}
      />
      <CounterCard
        to="/users"
        icon="fa-user-gear"
        accent="rose"
        label="Users"
        value={c.users?.active ?? 0}
        breakdown={[
          { label: 'Admins', value: c.users?.admins ?? 0 },
          { label: 'Total',  value: c.users?.total  ?? 0 },
        ]}
      />
    </div>
  );
}

const ACCENT_CLASSES = {
  indigo:  'bg-indigo-50 text-indigo-700',
  emerald: 'bg-emerald-50 text-emerald-700',
  violet:  'bg-violet-50 text-violet-700',
  cyan:    'bg-cyan-50 text-cyan-700',
  sky:     'bg-sky-50 text-sky-700',
  amber:   'bg-amber-50 text-amber-700',
  rose:    'bg-rose-50 text-rose-700',
};

function CounterCard({ to, icon, accent, label, value, breakdown }) {
  return (
    <Link to={to} className="card group flex flex-col gap-3 px-5 py-4 transition-colors hover:border-accent">
      <div className="flex items-center justify-between gap-3">
        <div className={`flex h-9 w-9 items-center justify-center rounded-xl ${ACCENT_CLASSES[accent] || 'bg-accent-soft text-accent'}`}>
          <i className={`fa-solid ${icon}`} aria-hidden="true" />
        </div>
        <i className="fa-solid fa-arrow-up-right-from-square text-[10px] text-mist opacity-0 transition-opacity group-hover:opacity-100" aria-hidden="true" />
      </div>
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-mist">{label}</div>
        <div className="text-2xl font-semibold tabular-nums text-ink">{value}</div>
      </div>
      {breakdown?.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-line pt-2 text-[11px]">
          {breakdown.map((b, i) => (
            <span key={i} className={`inline-flex items-center gap-1 ${b.warn ? 'text-amber-700' : 'text-mist'}`}>
              <span className="font-mono tabular-nums">{b.value}</span>
              <span className="opacity-80">{b.label}</span>
            </span>
          ))}
        </div>
      )}
    </Link>
  );
}

// ─── Coverage map ─────────────────────────────────────────────────────

function CoverageMap({ nodes, units }) {
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => {
    let cancelled = false;
    let map;

    async function mount() {
      if (!containerRef.current || mapRef.current) return;
      // Reuse the maplibregl asset path from the main map page; we don't
      // replicate the loading machinery here — we just wait for the global
      // window.maplibregl that the /map page or any prior visit will have
      // already set up, OR fetch the JS once.
      if (!window.maplibregl) {
        await new Promise((resolve, reject) => {
          let s = document.getElementById('maplibre-gl-script');
          if (s) { s.addEventListener('load', resolve, { once: true }); s.addEventListener('error', reject, { once: true }); return; }
          s = document.createElement('script');
          s.id = 'maplibre-gl-script';
          s.src = '/runtime-assets/vendor/maplibre/maplibre-gl.js';
          s.async = true;
          s.onload = resolve;
          s.onerror = reject;
          document.body.appendChild(s);
          // CSS too
          let l = document.getElementById('maplibre-gl-css');
          if (!l) {
            l = document.createElement('link');
            l.id = 'maplibre-gl-css'; l.rel = 'stylesheet'; l.href = '/runtime-assets/vendor/maplibre/maplibre-gl.css';
            document.head.appendChild(l);
          }
        }).catch(() => {});
      }
      if (cancelled || !window.maplibregl || !containerRef.current) return;

      let mvtApiKey = '';
      try {
        const r = await fetch('/api/config', { credentials: 'same-origin', cache: 'no-store' });
        if (r.ok) mvtApiKey = (await r.json()).mvtApiKey || '';
      } catch {}

      map = new window.maplibregl.Map({
        container: containerRef.current,
        style: `https://mvt.micro-gis.com/styles/positron${mvtApiKey ? '?key=' + encodeURIComponent(mvtApiKey) : ''}`,
        center: [10, 35],
        zoom: 1.8,
        attributionControl: false,
        interactive: true,
      });
      map.addControl(new window.maplibregl.NavigationControl({ showZoom: true, showCompass: false }), 'top-right');
      mapRef.current = map;

      map.on('load', () => fitAndPlot(map, nodes, units, markersRef));
    }

    mount();
    return () => {
      cancelled = true;
      markersRef.current.forEach((m) => { try { m.remove(); } catch {} });
      markersRef.current = [];
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-plot when data changes
  useEffect(() => {
    if (mapRef.current && mapRef.current.isStyleLoaded()) {
      fitAndPlot(mapRef.current, nodes, units, markersRef);
    }
  }, [nodes, units]);

  const empty = nodes.length === 0 && units.length === 0;

  return (
    <div className="card overflow-hidden">
      <header className="flex items-center justify-between border-b border-line px-5 py-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-ink">
          <i className="fa-solid fa-map-location-dot text-accent" aria-hidden="true" />
          Coverage map
        </div>
        <Link to="/map" className="text-xs text-accent hover:underline">
          Open full map <i className="fa-solid fa-arrow-up-right-from-square ml-1" aria-hidden="true" />
        </Link>
      </header>
      <div className="relative">
        <div ref={containerRef} style={{ height: 280, background: '#e5e7eb' }} />
        {empty && (
          <div className="absolute inset-0 flex items-center justify-center bg-canvas/80 text-sm text-mist">
            <i className="fa-solid fa-location-dot mr-2" aria-hidden="true" />
            No nodes or units have coordinates yet.
          </div>
        )}
      </div>
    </div>
  );
}

function fitAndPlot(map, nodes, units, markersRef) {
  // Wipe old markers
  markersRef.current.forEach((m) => { try { m.remove(); } catch {} });
  markersRef.current = [];

  const items = [
    ...nodes.map((n) => ({ ...n, kind: 'node' })),
    ...units.map((u) => ({ ...u, kind: 'unit' })),
  ];
  if (items.length === 0) return;

  for (const it of items) {
    const lat = Number(it.lat), lon = Number(it.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
    const el = document.createElement('div');
    el.style.cssText = `width:14px;height:14px;border-radius:50%;border:2px solid #1e293b;box-shadow:0 0 0 1px rgba(255,255,255,0.6);background:${it.kind === 'node' ? '#3388ff' : '#22c55e'};cursor:pointer;`;
    el.title = it.name || '';
    const m = new window.maplibregl.Marker({ element: el, anchor: 'center' })
      .setLngLat([lon, lat]).addTo(map);
    markersRef.current.push(m);
  }

  // Fit bounds
  try {
    const lons = items.map((i) => Number(i.lon)).filter(Number.isFinite);
    const lats = items.map((i) => Number(i.lat)).filter(Number.isFinite);
    if (lons.length === 0) return;
    const bbox = [[Math.min(...lons), Math.min(...lats)], [Math.max(...lons), Math.max(...lats)]];
    map.fitBounds(bbox, { padding: 32, maxZoom: 7, duration: 600 });
  } catch {}
}

// ─── Certificates panel ────────────────────────────────────────────────

function CertificatesPanel({ ca, stats }) {
  const caDays = daysUntil(ca?.notAfter);
  const caTone = caDays == null ? 'mute' : caDays <= 0 ? 'bad' : caDays < 90 ? 'warn' : 'good';
  return (
    <div className="card">
      <header className="flex items-center justify-between border-b border-line px-5 py-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-ink">
          <i className="fa-solid fa-shield text-accent" aria-hidden="true" />
          Identity authority
        </div>
        <Link to="/certificates" className="text-xs text-accent hover:underline">
          Open certificates <i className="fa-solid fa-arrow-up-right-from-square ml-1" aria-hidden="true" />
        </Link>
      </header>
      <div className="grid grid-cols-1 gap-4 px-5 py-4 sm:grid-cols-2">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-mist">Root CA</div>
          <div className="mt-1 break-all font-mono text-xs text-ink">{ca?.subject || '—'}</div>
          <div className="mt-2 flex items-center gap-2 text-xs text-mist">
            <span>Algorithm:</span>
            <code className="font-mono text-ink">{ca?.algorithm || '—'}</code>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
            <span className="text-mist">Expires:</span>
            <code className="font-mono text-ink">{ca?.notAfter ? new Date(ca.notAfter).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</code>
            {caDays != null && (
              <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${TONE_CLASSES[caTone]}`}>
                {caDays <= 0 ? 'expired' : `${caDays} days`}
              </span>
            )}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Stat label="Active"      value={stats?.active ?? 0} />
          <Stat label="Expiring 14d" value={stats?.expiringSoon ?? 0} warn={stats?.expiringSoon > 0} />
          <Stat label="Revoked"     value={stats?.revoked ?? 0} />
          <Stat label="Expired"     value={stats?.expired ?? 0} warn={stats?.expired > 0} />
          <Stat label="Total ever"  value={stats?.total ?? 0} />
          <Stat label="CRL #"       value={ca?.crlNumber ?? 0} mono />
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, warn, mono }) {
  return (
    <div className={`rounded-xl border px-3 py-2 ${warn ? 'border-amber-200 bg-amber-50' : 'border-line bg-canvas/40'}`}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-mist">{label}</div>
      <div className={`text-base font-semibold tabular-nums ${warn ? 'text-amber-800' : 'text-ink'} ${mono ? 'font-mono' : ''}`}>{value}</div>
    </div>
  );
}

// ─── Activity feed (recent audit) ─────────────────────────────────────

function ActivityFeed({ events, tz }) {
  return (
    <div className="card">
      <header className="flex items-center justify-between border-b border-line px-5 py-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-ink">
          <i className="fa-solid fa-clock-rotate-left text-accent" aria-hidden="true" />
          Recent activity
        </div>
        <Link to="/audit" className="text-xs text-accent hover:underline">
          Audit log <i className="fa-solid fa-arrow-up-right-from-square ml-1" aria-hidden="true" />
        </Link>
      </header>
      {events.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-mist">No recorded events yet.</div>
      ) : (
        <ul className="divide-y divide-line">
          {events.map((e) => (
            <li key={e.id} className="flex items-start gap-3 px-5 py-2.5 text-sm">
              <i className={`fa-solid ${eventIcon(e.eventType)} mt-0.5 w-4 text-mist`} aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-ink">{e.eventType}</span>
                  <OutcomeBadge outcome={e.outcome} />
                </div>
                <div className="truncate text-xs text-mist">
                  {e.actorLogin ? <span className="font-mono">{e.actorLogin}</span> : 'system'}
                  {e.targetKind && <> · target <code className="font-mono">{e.targetKind}{e.targetId ? '#' + e.targetId : ''}</code></>}
                </div>
              </div>
              <span className="shrink-0 text-[11px] text-mist" title={formatUtcInTimezone(e.ts, tz)}>
                {formatRelative(e.ts)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Notifications feed ───────────────────────────────────────────────

// Priority → visual tone. Schema is `routine | priority | immediate | flash`
// (see operational.js notifications table). Mapped to a 4-level loudness
// gradient so the operator can scan the feed and immediately spot urgencies.
const PRIORITY_TONE = {
  routine:   'border-sky-200    bg-sky-50    text-sky-700',
  priority:  'border-amber-200  bg-amber-50  text-amber-700',
  immediate: 'border-orange-300 bg-orange-50 text-orange-800',
  flash:     'border-rose-300   bg-rose-soft text-rose-800',
};

function NotificationsFeed({ items, tz }) {
  return (
    <div className="card">
      <header className="flex items-center justify-between border-b border-line px-5 py-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-ink">
          <i className="fa-solid fa-bell text-accent" aria-hidden="true" />
          Notifications
        </div>
        <Link to="/notifications" className="text-xs text-accent hover:underline">
          All <i className="fa-solid fa-arrow-up-right-from-square ml-1" aria-hidden="true" />
        </Link>
      </header>
      {items.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-mist">No notifications.</div>
      ) : (
        <ul className="divide-y divide-line">
          {items.map((n) => (
            <li key={n.id} className="flex items-start gap-3 px-5 py-2.5 text-sm">
              <span className={`inline-flex h-6 shrink-0 items-center rounded-full border px-2 text-[10px] font-semibold uppercase ${PRIORITY_TONE[n.priority] || PRIORITY_TONE.routine}`}>
                {n.priority || 'routine'}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-ink">{n.title || n.kind}</div>
                <div className="text-xs text-mist">{n.kind}</div>
              </div>
              <span className="shrink-0 text-[11px] text-mist" title={formatUtcInTimezone(n.created_at, tz)}>
                {formatRelative(n.created_at)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Footer status strip ──────────────────────────────────────────────

function FooterStrip({ data, tz }) {
  const cByCountry = data.counters?.nodesByCountry || [];
  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
      <div className="card flex items-center gap-4 px-5 py-3">
        <i className="fa-solid fa-server text-mist" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-mist">Server time</div>
          <div className="font-mono text-xs text-ink">{formatUtcInTimezone(data.serverTime, tz)}</div>
        </div>
      </div>
      <div className="card flex flex-wrap items-center gap-3 px-5 py-3 lg:col-span-2">
        <i className="fa-solid fa-globe text-mist" aria-hidden="true" />
        <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-mist">Top countries</div>
        {cByCountry.length === 0 ? (
          <span className="text-xs text-mist">—</span>
        ) : (
          cByCountry.map((c) => (
            <span key={c.code} className="inline-flex items-center gap-1.5 rounded-full border border-line bg-canvas/40 px-2.5 py-0.5 text-[11px]">
              <code className="font-mono uppercase text-ink">{c.code}</code>
              <span className="text-mist">{c.n}</span>
            </span>
          ))
        )}
      </div>
    </div>
  );
}
